import type { ReadonlyVec2 } from 'gl-matrix';

import type { EntityId } from '@canvas/model/ecs';
import type { InitialEditorTarget } from '@canvas/model/ui';

import { element } from './util.ts';

/**
 * Controls how a click on a rendered direct-edit slide target is exposed to the input
 * state machine.
 *
 * Direct-edit roots visually represent the whole slide child, so pointerdown uses
 * `ignore` to keep outer-entity selection and resize handles reachable. A later click
 * can use `text` to focus the same target directly once the outer child is selected.
 */
export type DirectEditRootSlideTargetPolicy = 'default' | 'ignore' | 'text';

export type GetInitialEditorTargetOptions = {
  directEditRootSlideTarget?: DirectEditRootSlideTargetPolicy;
};

/** Arguments passed to each resolver when trying to identify what was clicked. */
type InitialEditorTargetResolverArgs = GetInitialEditorTargetOptions & {
  targetElement: HTMLElement | SVGElement;
  clientX: number;
  clientY: number;
};

/** An editor target and the DOM element that represents it. */
interface ResolvedInitialEditorTarget {
  initialEditorTarget: InitialEditorTarget;
  targetElement: Element;
}

/** An editor target and its optional position within the containing entity. */
export interface InitialEditorTargetCapture {
  /** The editor target identified from the DOM. */
  initialEditorTarget: InitialEditorTarget;
  /** Entity whose DOM contains the editor target. */
  initialEditorTargetEntityId?: EntityId;
  /** Normalized top-center point of the target within the containing entity. */
  initialEditorToolbarAnchor?: ReadonlyVec2;
}

/** A function that checks whether a click hit a recognisable editor target. */
type InitialEditorTargetResolver = (
  args: InitialEditorTargetResolverArgs,
) => ResolvedInitialEditorTarget | undefined;

const slideTargetSelector =
  '[data-slide-node="slideTarget"][data-slide-id], [data-slide-node="slideTarget"][data-slide-target-id]';

/**
 * Identifies a clicked slide target, then reads the target ID from either the rendered
 * slide DOM (`data-slide-id`) or a mounted editor node view (`data-slide-target-id`).
 *
 * The rendered and mounted IDs share one path because both DOM shapes represent the
 * same logical slide target. The direct-edit-root policy only applies to rendered
 * slide output; mounted editor targets should always be reported to support switching
 * selected targets inside an active editor.
 */
const resolveSlideTargetInitialEditorTarget: InitialEditorTargetResolver = ({
  targetElement,
  clientX,
  clientY,
  directEditRootSlideTarget = 'default',
}) => {
  const editorTargetElement = targetElement.closest(slideTargetSelector);
  if (!editorTargetElement) {
    return undefined;
  }

  const renderedTargetId = editorTargetElement.getAttribute('data-slide-id');
  const mountedEditorTargetId = editorTargetElement.getAttribute('data-slide-target-id');
  const targetId = renderedTargetId || mountedEditorTargetId;

  if (!targetId) {
    return undefined;
  }

  let initialSelectionMode: 'text' | undefined;
  if (renderedTargetId && isRenderedDirectEditRootTarget(editorTargetElement)) {
    if (directEditRootSlideTarget === 'ignore') {
      return undefined;
    }

    initialSelectionMode = directEditRootSlideTarget === 'text' ? 'text' : undefined;
  }

  return {
    targetElement: editorTargetElement,
    initialEditorTarget: {
      targetType: 'slideTarget',
      targetId,
      clientX,
      clientY,
      ...(initialSelectionMode ? { initialSelectionMode } : {}),
    },
  };
};

/**
 * Returns true when the clicked rendered target belongs to a direct-edit slide root.
 *
 * The renderer writes `data-slide-direct-edit-root` from the slide capability registry,
 * which is the source of truth used by the mounted slides editor. Keeping this as an
 * attribute read prevents the input package from inferring slide behaviour by counting
 * DOM nodes.
 */
const isRenderedDirectEditRootTarget = (targetElement: Element): boolean =>
  targetElement
    .closest('[data-slide-node="slideElement"]')
    ?.getAttribute('data-slide-direct-edit-root') === 'true';

/**
 * Resolvers tried in order to identify which text box was clicked. Each checks the DOM
 * for a specific pattern (e.g. slide targets). To support new element types, add a
 * resolver here.
 */
const initialEditorTargetResolvers: InitialEditorTargetResolver[] = [
  resolveSlideTargetInitialEditorTarget,
];

/**
 * Calculates the target's top-center point as normalized coordinates within its entity's
 * bounds.
 */
const getInitialEditorToolbarAnchor = (
  targetElement: Element,
): ReadonlyVec2 | undefined => {
  const entityElement = targetElement.closest<HTMLElement>('[data-entity-id]');
  if (!entityElement) {
    return undefined;
  }

  const entityRect = entityElement.getBoundingClientRect();
  if (entityRect.width === 0 || entityRect.height === 0) {
    return undefined;
  }

  const targetRect = targetElement.getBoundingClientRect();

  return [
    (targetRect.left - entityRect.left + targetRect.width / 2) / entityRect.width,
    (targetRect.top - entityRect.top) / entityRect.height,
  ];
};

/**
 * Determines which node of the rendered ADF document the editor should select after a
 * pointer event.
 *
 * The result describes the node and includes its normalized top-center point relative to
 * the containing entity.
 */
export const getInitialEditorTarget = (
  target: EventTarget | null,
  clientX: number,
  clientY: number,
  options: GetInitialEditorTargetOptions = {},
): InitialEditorTargetCapture | undefined => {
  const targetElement = element(target);
  if (!targetElement) {
    return undefined;
  }

  for (const resolver of initialEditorTargetResolvers) {
    const resolvedTarget = resolver({
      targetElement,
      clientX,
      clientY,
      directEditRootSlideTarget: options.directEditRootSlideTarget,
    });
    if (resolvedTarget) {
      const entityId = resolvedTarget.targetElement
        .closest<HTMLElement>('[data-entity-id]')
        ?.getAttribute('data-entity-id');
      const initialEditorToolbarAnchor = getInitialEditorToolbarAnchor(
        resolvedTarget.targetElement,
      );

      return {
        initialEditorTarget: resolvedTarget.initialEditorTarget,
        initialEditorTargetEntityId: entityId as EntityId | undefined,
        initialEditorToolbarAnchor,
      };
    }
  }

  return undefined;
};
